import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Code, LogOut, Key, BookOpen, CheckCircle2, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeveloperDashboard = () => {
  const navigate = useNavigate();
  const [developer, setDeveloper] = useState(null);
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('dev_token');
    localStorage.removeItem('dev_email');
    toast.success('Logout effettuato');
    navigate('/developer-auth');
  }, [navigate]);

  const fetchDeveloperData = useCallback(async () => {
    try {
      const token = localStorage.getItem('dev_token');
      const response = await axios.get(`${API}/developers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeveloper(response.data);
    } catch (error) {
      console.error('Error fetching developer data:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta, effettua nuovamente il login');
        handleLogout();
      }
    }
  }, [handleLogout]);

  const fetchApis = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/apis`);
      setApis(response.data);
    } catch (error) {
      console.error('Error fetching APIs:', error);
      toast.error('Errore nel caricamento delle API');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('dev_token');
    if (!token) {
      toast.error('Devi effettuare il login');
      navigate('/developer-auth');
      return;
    }

    fetchDeveloperData();
    fetchApis();
  }, [fetchDeveloperData, fetchApis, navigate]);

  const getStatusBadge = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle2 className="w-3 h-3" />
          Attiva
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <XCircle className="w-3 h-3" />
        Non attiva
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--accent-primary)' }}></div>
          <p style={{ color: 'var(--text-secondary)' }}>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-alt)' }}>
      {/* Header */}
      <nav className="nav-bar">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_ship-smart-6/artifacts/4h2rj0xf_logoonly.png" 
            alt="SpedireOnline.net" 
            className="h-8 w-8 object-contain"
          />
          <span className="font-bold text-xl" style={{ color: 'var(--brand-primary)' }}>spedireonline.net</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm hidden md:block" style={{ color: 'var(--text-secondary)' }}>
            {developer?.email}
          </span>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Benvenuto, {developer?.first_name}!
          </h1>
          <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
            Dashboard sviluppatore - Gestisci le tue API e credenziali
          </p>
        </div>

        {/* Developer Info Card */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {developer?.first_name} {developer?.last_name}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{developer?.email}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Azienda
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                {developer?.company || 'N/A'}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{developer?.role || 'N/A'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Stato Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Attivo
                </span>
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                Registrato il {new Date(developer?.created_at).toLocaleDateString('it-IT')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* API Credentials Section */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <CardTitle>Le mie Credenziali API</CardTitle>
            </div>
            <CardDescription>
              Le tue chiavi API sono gestite tramite il sistema esterno. Contatta il supporto per maggiori informazioni.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-4 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <p className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Gestione API Key Esterna
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Per ottenere le tue API keys, accedi al portale di gestione dedicato o contatta il supporto tecnico all'indirizzo{' '}
                    <a href="mailto:support@spedireonline.net" className="underline font-medium" style={{ color: 'var(--accent-primary)' }}>
                      support@spedireonline.net
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Available APIs Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                API Disponibili
              </h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                Esplora le nostre API e accedi alla documentazione
              </p>
            </div>
          </div>

          {apis.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Code className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                <p style={{ color: 'var(--text-secondary)' }}>Nessuna API disponibile al momento</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {apis.map((api) => (
                <Card key={api.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {api.name}
                          {getStatusBadge(api.is_active)}
                        </CardTitle>
                        <CardDescription className="mt-2">
                          {api.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Categoria:</span>
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {api.category}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: 'var(--text-secondary)' }}>Versione:</span>
                        <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                          {api.version}
                        </span>
                      </div>
                      {api.base_url && (
                        <div className="text-sm">
                          <span className="block mb-1" style={{ color: 'var(--text-secondary)' }}>Base URL:</span>
                          <code className="block p-2 rounded text-xs bg-gray-100 break-all" style={{ color: 'var(--text-primary)' }}>
                            {api.base_url}
                          </code>
                        </div>
                      )}
                      <div className="pt-3 flex gap-2">
                        <Button 
                          variant="default" 
                          className="flex-1"
                          onClick={() => navigate('/documentazione-api')}
                          disabled={!api.is_active}
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Documentazione
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <Card>
          <CardHeader>
            <CardTitle>Hai bisogno di aiuto?</CardTitle>
            <CardDescription>
              Il nostro team di supporto è qui per aiutarti
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <p className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Documentazione Completa
                  </p>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Guide dettagliate e esempi di codice
                  </p>
                  <Button variant="link" className="p-0 h-auto" onClick={() => navigate('/developers')}>
                    Vai alla documentazione
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: 'var(--accent-primary)' }} />
                <div>
                  <p className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Supporto Tecnico
                  </p>
                  <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Il nostro team risponde entro 24h
                  </p>
                  <a href="mailto:support@spedireonline.net" className="text-sm font-medium underline" style={{ color: 'var(--accent-primary)' }}>
                    support@spedireonline.net
                  </a>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DeveloperDashboard;
